/**
 * The authoritative record of what Éros actually DID on a turn.
 *
 * Rendered entirely from the tool's own result (BE `receipts`, derived from the
 * executor's coded status — never from the reply text). The prose above is the
 * model's claim; this is the outcome. When a write is refused the row reads ✗
 * even if the text says otherwise — which is exactly the moment it matters.
 *
 * The invariant this exists to serve: EVERY write renders exactly one row, so
 * "no receipt = nothing happened" is readable. That is why an unmapped tool
 * falls back to generic copy instead of rendering nothing — a silent gap would
 * teach the couple that absence means nothing. `receiptCopyKey` owns that rule
 * and is covered by a parity test against the BE's write-tool list.
 */

import { Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import IconifyIcon from 'components/base/IconifyIcon';
import type { WriteReceipt } from 'services/api/assistant';

/**
 * i18n key for one receipt row. Specific copy where the detail is worth having;
 * the generic fallback keeps the invariant whole for every other write tool.
 */
export const receiptCopyKey = (receipt: WriteReceipt): string => {
  if (!receipt.ok) return 'assistant.receipt.failed';
  const specific = RECEIPT_BY_TOOL[receipt.tool];
  return specific ?? 'assistant.receipt.done';
};

const RECEIPT_BY_TOOL: Record<string, string> = {
  apply_seating_plan: 'assistant.receipt.seating_applied',
  revert_seating_plan: 'assistant.receipt.seating_reverted',
  seat_guests: 'assistant.receipt.guests_seated',
  move_guests: 'assistant.receipt.guests_moved',
  unseat_guests: 'assistant.receipt.guests_unseated',
  set_seating_preferences: 'assistant.receipt.preference_saved',
};

/**
 * Drop rows that would render the SAME words as one already shown.
 *
 * Recording one symmetric wish («ο Νίκος με την Ιωάννα») can cost two
 * set_seating_preferences calls, and two identical «Αποθηκεύτηκε» rows carry no
 * more information than one — they read as clutter and make the couple wonder
 * what the second thing was.
 *
 * This does NOT weaken the invariant. The invariant is "no receipt = no write";
 * it never claimed one row per call. Keyed on the rendered copy + outcome +
 * params, so a ✓ and a ✗, two different tools, or two saves with different
 * details are never merged — only rows that are literally the same sentence.
 */
const dedupeReceipts = (receipts: WriteReceipt[]): WriteReceipt[] => {
  const seen = new Set<string>();
  return receipts.filter((r) => {
    const key = `${receiptCopyKey(r)}|${r.ok}|${JSON.stringify(r.params ?? {})}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

interface AssistantWriteReceiptProps {
  receipts: WriteReceipt[];
}

const AssistantWriteReceipt = ({ receipts }: AssistantWriteReceiptProps) => {
  const { t } = useTranslation();
  const rows = dedupeReceipts(receipts);

  return (
    <Stack direction="column" spacing={0.5} sx={{ pl: 0.5 }}>
      {rows.map((receipt, i) => (
        <Stack key={`${receipt.tool}-${i}`} direction="row" spacing={0.75} alignItems="center">
          <IconifyIcon
            icon={
              receipt.ok
                ? 'material-symbols:check-circle-outline-rounded'
                : 'material-symbols:cancel-outline-rounded'
            }
            sx={{
              fontSize: 15,
              color: receipt.ok ? 'success.main' : 'error.main',
              flexShrink: 0,
            }}
          />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {t(receiptCopyKey(receipt), { ...receipt.params })}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
};

export default AssistantWriteReceipt;
